import $ from 'jquery'
import Fonts from './fonts'

/**
 * ------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------
 */

const NAME = 'fontsetpreview'
const DATA_KEY = `opp.${NAME}`
const JQUERY_NO_CONFLICT = $.fn[NAME]

const ClassName = {
  PREVIEW_TITLE: 'font-set-preview-text-title',
  PREVIEW_NAVIGATION: 'font-set-preview-text-navigation',
  PREVIEW_HEADING: 'font-set-preview-text-heading',
  PREVIEW_BODY: 'font-set-preview-text-body'
}

const Selector = {
  DATA_INIT: '[data-init^="font-set-preview"]',
  PREVIEW_DATA: '.font-set-preview-data',
  PREVIEW_TEXT: '[class*="font-set-preview-text-"]'
}

const PREVIEW_CLASS_RE = /(?:^| )(font-set-preview-text-(?:[a-z]+))(?: |$)/

const FontKeyMap = {
  [ClassName.PREVIEW_TITLE]: 'title_font',
  [ClassName.PREVIEW_NAVIGATION]: 'navigation_font',
  [ClassName.PREVIEW_HEADING]: 'heading_font',
  [ClassName.PREVIEW_BODY]: 'body_font'
}

const FontSizeKeyMap = {
  [ClassName.PREVIEW_TITLE]: 'title_font_size',
  [ClassName.PREVIEW_NAVIGATION]: 'navigation_font_size',
  [ClassName.PREVIEW_HEADING]: 'heading_font_size',
  [ClassName.PREVIEW_BODY]: 'body_font_size'
}

/**
 * ------------------------------------------------------------------------
 * Class Definition
 * ------------------------------------------------------------------------
 */

class FontSetPreview {
  constructor(element) {
    this._element = element
    this._document = element.ownerDocument
    this._fontSet = this._getFontSet()
    // N.B. do not load here
  }

  // Public

  show(headFrag) {
    if (this._fontSet) {
      // Update element if font has changed
      if (this._fontSet.id !== $(this._element).data('font-id')) {
        $(this._element).data('font-id', this._fontSet.id)
        $(this._element).find(Selector.PREVIEW_TEXT).each((_, elem) => {
          const font = this._getFontForPreviewElem(elem)
          if (font) {
            this._loadFont(font, $(elem).text(), headFrag)

            const fontSize = this._getFontSizeForPreviewElem(elem)
            $(elem).css({ ...Fonts.styleProps(font), fontSize })
          }
        })

        // Show optional collapse
        $(this._element).closest('.collapse').collapse('show')
      }
    }
  }

  // Private

  _getFontSet() {
    const el = this._element.querySelector(Selector.PREVIEW_DATA)
    if (el && el.text) {
      return JSON.parse(el.text)
    }
  }

  _getPreviewClassForElem(elem) {
    if (!this._fontSet || !elem) {
      return
    }

    const match = PREVIEW_CLASS_RE.exec(elem.className)
    if (!match) {
      return
    }

    return match[1]
  }

  _getFontForPreviewElem(elem) {
    const previewClass = this._getPreviewClassForElem(elem)
    const fontKey = FontKeyMap[previewClass]
    return this._fontSet[fontKey]
  }

  _getFontSizeForPreviewElem(elem) {
    const previewClass = this._getPreviewClassForElem(elem)
    const fontSizeKey = FontSizeKeyMap[previewClass]
    const fontSizePx = parseInt(this._fontSet[fontSizeKey], 10) || 14
    // Scale up by %25 and convert to REM
    return `${fontSizePx * 1.25 / 14}rem`
  }

  _loadFont(font, text, targetNode) {
    if (font && !font._loaded) {
      font._loaded = true
      $(this._document).fontloader('load', font, text, targetNode)
    }
  }

  // Static

  static _jQueryInterface(config, ...args) {
    const instances = this.map(function () {
      let data = $(this).data(DATA_KEY)
      if (!data) {
        data = new FontSetPreview(this)
        $(this).data(DATA_KEY, data)
      }

      return data
    })

    if (instances && instances.length > 0) {
      // Group updates when showing more than one instance.
      if (config === 'show' && this.length > 1) {
        const { _document } = instances[0]
        const headFrag = _document.createDocumentFragment()
        instances.each(function () {
          this.show(headFrag)
        })
        _document.head.appendChild(headFrag)
      } else if (typeof config === 'string') {
        instances.each(function () {
          if (typeof this[config] === 'undefined') {
            throw new TypeError(`No method named "${config}"`)
          }

          this[config](...args)
        })
      }
    }

    return this
  }
}

/**
 * ------------------------------------------------------------------------
 * Data Api implementation
 * ------------------------------------------------------------------------
 */

FontSetPreview._jQueryInterface.call($(Selector.DATA_INIT), 'show')

/**
 * ------------------------------------------------------------------------
 * jQuery
 * ------------------------------------------------------------------------
 */

$.fn[NAME] = FontSetPreview._jQueryInterface
$.fn[NAME].Constructor = FontSetPreview
$.fn[NAME].noConflict = () => {
  $.fn[NAME] = JQUERY_NO_CONFLICT
  return FontSetPreview._jQueryInterface
}

export default FontSetPreview
